'use client'

import type { ExportKey } from '@/modules/export/hooks/useExports'
import { useForm } from '@/shared/hooks/useForm'
import type { UseMutationResult } from '@tanstack/react-query'
import { subMonths, subWeeks } from 'date-fns'
import { pick } from 'lodash'
import { useTranslations } from 'next-intl'
import { Controller } from 'react-hook-form'
import { z } from 'zod'
import Button from './Button'
import Datepicker from './Datepicker/Datepicker'
import { Field } from '@hello-aria/ui/field'
import { RadioGroup } from '@hello-aria/ui/radio-group'
import Dialog from './Dialog'
import Icon from './Icon'
import { Loader } from 'lucide-react'
import { useState } from 'react'

const ExportScopeEnum = z.enum(['withoutFilters', 'withFilters'])
const NonCustomRangeEnum = z.enum(['7', '30', '90', 'all', 'custom'])

const ExportDataSchema = z.object({
    scope: ExportScopeEnum.default('withoutFilters'),
    range: NonCustomRangeEnum.default('7'),
    startDate: z.date().nullable(),
    endDate: z.date().nullable()
})

export type ExportDataForm = z.infer<typeof ExportDataSchema>

export type ExportData = {
    scope: ExportDataForm['scope']
    startDate: Date | null
    endDate: Date | null
}

export type ExportDataDialogProps = {
    namespace: ExportKey
    status: UseMutationResult['status']
    isMutating: boolean
    onSubmit: (payload: ExportData) => void
}

const ExportDataDialog = ({ namespace, isMutating, onSubmit }: ExportDataDialogProps) => {
    const t = useTranslations('root')
    const today = new Date()
    const [isOpen, setIsOpen] = useState(false)

    const { control, watch, register, reset, setValue, handleSubmit } = useForm({
        schema: ExportDataSchema,
        defaultValues: {
            scope: 'withoutFilters',
            range: '7',
            startDate: null,
            endDate: null
        }
    })

    const _onSubmit = (payload: ExportDataForm) => {
        const buildDate = () => {
            switch (payload.range) {
                case '7':
                    return { startDate: subWeeks(today, 1), endDate: today }
                case '30':
                    return { startDate: subMonths(today, 1), endDate: today }
                case '90':
                    return { startDate: subMonths(today, 3), endDate: today }
                case 'custom':
                    return { startDate: payload.startDate, endDate: payload.endDate }
                case 'all':
                default:
                    return { startDate: null, endDate: null }
            }
        }
        const mappedPayload: ExportData = {
            ...buildDate(),
            scope: payload.scope
        }

        onSubmit(mappedPayload)
        onOpenChange(false)
    }

    const onOpenChange = (isOpen: boolean) => {
        setIsOpen(isOpen)

        if (!isOpen) {
            reset()
        }
    }

    return (
        <Dialog.Root open={isOpen} onOpenChange={onOpenChange}>
            <Dialog.Trigger asChild>
                <Button data-testid="__export-data-dialog-trigger__" intent="secondary">
                    {isMutating ? (
                        <Icon
                            data-testid="__export-data-dialog-mutating-icon__"
                            icon={Loader}
                            className="w-4 h-4 mr-2 animate-spin"
                        />
                    ) : null}
                    {t('components.exportDialog.button')}
                </Button>
            </Dialog.Trigger>
            <Dialog.Content
                data-testid="__export-data-dialog-content__"
                title={t(`components.exportDialog.title.${namespace}`)}
                className="max-w-100"
            >
                <Dialog.Description className="sr-only">{t('components.exportDialog.description')}</Dialog.Description>
                <form onSubmit={handleSubmit(_onSubmit)}>
                    <Dialog.Body className="grid grid-cols-1 gap-4">
                        <Controller
                            name="scope"
                            control={control}
                            render={({ field: { value, onChange } }) => (
                                <Field label={t('components.exportDialog.subTitle')} isControlLabelled>
                                    <RadioGroup
                                        value={value}
                                        onValueChange={onChange}
                                        options={[
                                            {
                                                value: 'withoutFilters',
                                                label: t('components.exportDialog.options.allData')
                                            },
                                            {
                                                value: 'withFilters',
                                                label: t('components.exportDialog.options.filteredData')
                                            }
                                        ]}
                                    />
                                </Field>
                            )}
                        />

                        <Controller
                            name="range"
                            control={control}
                            render={({ field: { value, onChange } }) => (
                                <Field label={t('components.exportDialog.range')} isControlLabelled>
                                    <RadioGroup
                                        value={value}
                                        onValueChange={(range) => {
                                            if (range !== 'custom') {
                                                setValue('startDate', null)
                                                setValue('endDate', null)
                                            }
                                            onChange(range)
                                        }}
                                        options={NonCustomRangeEnum.options.map((range) => ({
                                            value: range,
                                            label: t(`components.exportDialog.options.${range}`)
                                        }))}
                                    />
                                </Field>
                            )}
                        />
                        {watch('range') === 'custom' && (
                            <div className="flex flex-col gap-2">
                                <Controller
                                    control={control}
                                    name="startDate"
                                    render={({ field: { value, onChange } }) => (
                                        <Datepicker
                                            {...pick(register('startDate'), ['required', 'message', 'state'])}
                                            data-testid="__export-data-dialog-start-date__"
                                            label={t('components.exportDialog.startDateLabel')}
                                            placeholder={t('components.exportDialog.datesPlaceholder')}
                                            maxDate={watch('endDate')}
                                            openToDate={watch('endDate') ?? today}
                                            onChange={onChange}
                                            value={value ? new Date(value) : null}
                                        />
                                    )}
                                />
                                <Controller
                                    control={control}
                                    name="endDate"
                                    render={({ field: { value, onChange } }) => (
                                        <Datepicker
                                            {...pick(register('endDate'), ['required', 'message', 'state'])}
                                            data-testid="__export-data-dialog-end-date__"
                                            label={t('components.exportDialog.endDateLabel')}
                                            placeholder={t('components.exportDialog.datesPlaceholder')}
                                            minDate={watch('startDate')}
                                            openToDate={watch('startDate') ?? today}
                                            onChange={onChange}
                                            value={value ? new Date(value) : null}
                                        />
                                    )}
                                />
                            </div>
                        )}
                    </Dialog.Body>
                    <Dialog.Footer>
                        <Dialog.Close asChild>
                            <Button type="button" intent="secondary" data-testid="__export-data-dialog-cancel__">
                                {t('components.exportDialog.actions.cancel')}
                            </Button>
                        </Dialog.Close>

                        <Button type="submit" intent="primary" data-testid="__export-data-dialog-submit__">
                            {t('components.exportDialog.actions.export')}
                        </Button>
                    </Dialog.Footer>
                </form>
            </Dialog.Content>
        </Dialog.Root>
    )
}

export default ExportDataDialog
